import { useState } from 'react';

import dayjs, { Dayjs } from 'dayjs';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

import { TAX_INVOICE_TYPE } from '~apis/tax-invoice';
import { getMemoizedPreset } from '~components/ui/DatePicker/DateRangePickerWithPreset';
import { usePageHeight } from '~hooks/layout/usePageHeight';
import { useGetInvoiceMappings } from '~hooks/queries/mapping/query';
import { useDemoAwareCompanyId } from '~hooks/useDemoAwareCompanyId';
import { usePageWidth } from '~hooks/usePageWidth';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';
import { PAGE_NAME as PURCHASE_SETTLEMENT_PAGE_NAME } from '~pages/clobe/purchase-settlement';
import { PAGE_NAME as SALES_SETTLEMENT_PAGE_NAME } from '~pages/clobe/sales-settlement';

import SettlementListHeader from './SettlementListHeader';
import SettlementListTable from './SettlementListTable';
import { SettlementStatus } from './utils';
import { LayoutWrapper } from '../../styles/global';

interface Props {
  type: TAX_INVOICE_TYPE;
  businessEntityId?: number;
  defaultStartDate?: Dayjs;
  defaultEndDate?: Dayjs;
}

const SettlementList: React.FC<Props> = ({
  type,
  businessEntityId,
  defaultStartDate,
  defaultEndDate,
}) => {
  const [sort, setSort] = useState<string[]>([]);
  const [status, setStatus] = useState<SettlementStatus>();
  const { companyId } = useDemoAwareCompanyId();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(300);
  const height = usePageHeight(0);
  const pageWidth = usePageWidth(0);
  const [searchParam, setSearchParam] = useState<string>('');
  const [rangeDateValue, setRangeDateValue] = useState<[Dayjs, Dayjs]>([
    defaultStartDate || getMemoizedPreset()?.value[0] || dayjs().subtract(2, 'M').startOf('month'),
    defaultEndDate || getMemoizedPreset()?.value[1] || dayjs().endOf('month'),
  ]);

  const { data, refetch } = useGetInvoiceMappings({
    companyId,
    requestParams: {
      startDate: rangeDateValue[0].format('YYYY-MM-DD'),
      endDate: rangeDateValue[1].format('YYYY-MM-DD'),
      type,
      status,
      page,
      size: pageSize,
      searchParam,
      sort,
      businessEntityIds: businessEntityId ? [businessEntityId] : [],
    },
  });

  const invoiceMappings = data?.Data?.data?.content || [];
  const totalElements = data?.Data?.data?.totalElements || 0;

  return (
    <OverlayScrollbarsComponent
      style={{
        width: '100%',
        maxWidth: pageWidth,
        height,
        overflowY: 'auto',
      }}
    >
      <LayoutWrapper>
        <SettlementListHeader
          type={type}
          rangeDateValue={rangeDateValue}
          setRangeDateValue={setRangeDateValue}
          onSearch={(value) => {
            setPage(0);
            setSearchParam(value);
            mixpanel.track(MIXPANEL_EVENT.GLOBAL.SEARCH, {
              page: type === 'SALES' ? SALES_SETTLEMENT_PAGE_NAME : PURCHASE_SETTLEMENT_PAGE_NAME,
              type: type === 'SALES' ? 'sales-settlement' : 'purchase-settlement',
              searchParam: value,
            });
          }}
          setStatus={(status) => setStatus(status)}
          activeStatus={status}
          searchParam={searchParam}
          sort={(sort.at(0)?.length ?? 0) > 0 ? sort.at(0) : undefined}
        />
      </LayoutWrapper>
      <SettlementListTable
        startDate={rangeDateValue[0]}
        endDate={rangeDateValue[1]}
        items={invoiceMappings}
        totalElements={totalElements}
        page={page}
        setPage={setPage}
        size={pageSize}
        setSize={setPageSize}
        type={type}
        onUpdatePaymentDate={refetch}
        onMappingSuccess={refetch}
        setSort={setSort}
      />
    </OverlayScrollbarsComponent>
  );
};

export default SettlementList;
